import { convertToDecimals } from "../../../utils/utils";
import {
  THRESHOLD_UNIT,
  normalizeUnit,
  isCountUnit,
  formatUnitLabel,
  pluralizeUnit
} from "../../../utils/payRequestUtils";

// Count units (shift, block, game, ...) are hours-style (cumulative quantity, never day-index).
// Days still pays from hours.

export const isHoursStyleUnit = (unitType) =>
  normalizeUnit(unitType) === THRESHOLD_UNIT.HOURS || isCountUnit(unitType);

export const getEntryQuantity = (entry, unitType) => {
  if (isCountUnit(unitType)) {
    return convertToDecimals(entry?.shifts ?? 0);
  }
  return convertToDecimals((entry?.hours || 0) + convertToDecimals((entry?.minutes || 0) / 60));
};

export const sumEntryQuantity = (entries, unitType) =>
  (entries || []).reduce((sum, entry) => sum + getEntryQuantity(entry, unitType), 0);

export const isSameTimesheetQuantity = (existing, form, unitType) => {
  if (isCountUnit(unitType)) {
    return convertToDecimals(existing?.shifts) === convertToDecimals(form?.shifts);
  }
  return existing?.hours === form?.hours && existing?.minutes === form?.minutes;
};

export const hasValidQuantity = (form, unitType) => {
  if (isCountUnit(unitType)) {
    return convertToDecimals(form?.shifts) > 0;
  }
  return form?.hours !== 0 || form?.minutes !== 0;
};

export const getQuantityValidationMessage = (unitType) => {
  if (isCountUnit(unitType)) {
    return `${formatUnitLabel(pluralizeUnit(unitType))} must be greater than 0.`;
  }
  return "Hours and minutes cannot both be 0. Please enter at least 1 hour or 1 minute.";
};

export const getTimesheetQuantityFields = (form, unitType) => {
  if (isCountUnit(unitType)) {
    return {
      in_hours: 0,
      in_minutes: 0,
      in_shifts: convertToDecimals(form?.shifts)
    };
  }
  return {
    in_hours: form?.hours,
    in_minutes: form?.minutes,
    in_shifts: null
  };
};

export const formatTimesheetQuantity = (row, { capitalizeUnit = false } = {}) => {
  const unitType = row?.thresold_unit_type;
  const shifts = convertToDecimals(row?.shifts ?? 0);

  if (isCountUnit(unitType)) {
    return `${shifts} ${capitalizeUnit ? formatUnitLabel(unitType) : normalizeUnit(unitType)}(s)`;
  }
  if (!unitType && shifts > 0) {
    return `${shifts}${capitalizeUnit ? "Shift" : "shift"}(s)`;
  }

  return `${row?.hours || 0}h ${row?.minutes || 0}m`;
};

export const getRateUnitSuffix = (unitType) =>
  isCountUnit(unitType) ? `/${normalizeUnit(unitType)}` : "/hr";

export const formatTierRateTooltip = (tier) => {
  const unitType = normalizeUnit(tier?.thresold_unit_type);
  const min = tier?.min_thresold || 0;
  const range = tier?.max_thresold ? `${min}-${tier.max_thresold}` : `${min}+`;

  if (isCountUnit(unitType)) {
    const rate = Number(tier?.rate || 0).toFixed(2);
    return `${formatUnitLabel(pluralizeUnit(unitType))} ${range}: $${rate}/${unitType}`;
  }

  const isDaily = tier?.rate_unit_type === "daily";
  const rate = Number(isDaily ? Number(tier?.rate || 0) * 24 : Number(tier?.rate || 0)).toFixed(2);
  const rangeLabel = unitType === THRESHOLD_UNIT.DAYS ? "Days" : "Hours";
  const unit = isDaily ? "day" : "hr";
  return `${rangeLabel} ${range}: $${rate}/${unit}`;
};
